import React, { useState, useEffect } from 'react';
import { holidayAPI, holidayGroupAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Card, ModernButton, ModernInput, ModernSelect, Badge, LoadingSpinner } from '../components/SharedComponents';
import { FiGift, FiPlus, FiX } from '../components/Icons';

const Container = styled.div`
  width: 100%;
  padding: ${(p) => p.theme.spacing.lg};
  box-sizing: border-box;
  max-width: none;
`;

const HeaderCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing.xl};

  h1 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm};
  }

  p {
    margin: ${(p) => p.theme.spacing.xs} 0 0 0;
    color: ${(p) => p.theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.md};
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.text.secondary};
  }
`;

const Chips = styled.div`
  display: inline-flex;
  border: 1px solid ${(p) => p.theme.colors.border.medium};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  overflow: hidden;

  button {
    border: none;
    background: ${(p) => p.theme.colors.surface};
    color: ${(p) => p.theme.colors.text.secondary};
    padding: 8px 16px;
    font-size: 0.88rem;
    font-weight: 600;
    font-family: ${(p) => p.theme.fonts.main};
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    & + button {
      border-left: 1px solid ${(p) => p.theme.colors.border.medium};
    }

    &.active {
      background: ${(p) => p.theme.colors.primary};
      color: ${(p) => p.theme.colors.text.inverse};
    }
  }
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${(p) => p.theme.fonts.main};

  th, td {
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid ${(p) => p.theme.colors.border.light};
  }

  th {
    background: ${(p) => p.theme.colors.surfaceAlt};
    color: ${(p) => p.theme.colors.text.secondary};
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  td {
    font-size: 0.92rem;
    color: ${(p) => p.theme.colors.text.primary};
    vertical-align: middle;
  }

  tbody tr {
    transition: background 0.12s ease;

    &:hover { background: ${(p) => p.theme.colors.surfaceAlt}; }
    &:last-child td { border-bottom: none; }
    &.past td { color: ${(p) => p.theme.colors.text.light}; }
  }
`;

const CountNote = styled.div`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.text.secondary};
  margin-bottom: ${(p) => p.theme.spacing.sm};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(p) => p.theme.spacing.xxl};
  color: ${(p) => p.theme.colors.text.secondary};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(43, 40, 37, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: ${(p) => p.theme.spacing.md};
`;

const ModalContent = styled.div`
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.large};
  box-shadow: ${(p) => p.theme.shadows.heavy};
  padding: ${(p) => p.theme.spacing.xl};
  max-width: 440px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;

  h3 { margin: 0; font-size: 1.25rem; }
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  background: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.colors.surfaceAlt};
    color: ${(p) => p.theme.colors.text.primary};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${(p) => p.theme.spacing.sm};
  margin-top: ${(p) => p.theme.spacing.xl};
  padding-top: ${(p) => p.theme.spacing.lg};
  border-top: 1px solid ${(p) => p.theme.colors.border.light};
`;

const parseApiError = (error) => {
  const data = error.response?.data;
  if (!data) return error.message || 'Failed to save holiday';
  const fmt = (arr) => arr.map((e) => (typeof e === 'object' && e.msg ? `${e.loc ? e.loc.join('.') + ': ' : ''}${e.msg}` : String(e))).join('; ');
  if (Array.isArray(data)) return fmt(data);
  if (Array.isArray(data.detail)) return fmt(data.detail);
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.message === 'string') return data.message;
  return 'Validation error — please check your input.';
};

const PublicHolidaysPage = () => {
  const { hasRole } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [holidayGroups, setHolidayGroups] = useState([]);
  const [selectedHolidayGroup, setSelectedHolidayGroup] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({ name: '', date: '', holiday_group_id: '' });

  const canManage = hasRole(['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']);

  useEffect(() => {
    loadHolidayGroups();
  }, []);

  useEffect(() => {
    if (selectedHolidayGroup) loadHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHolidayGroup, selectedYear]);

  const loadHolidayGroups = async () => {
    try {
      const response = await holidayGroupAPI.getAll();
      setHolidayGroups(response.data);
      if (response.data.length > 0) {
        setSelectedHolidayGroup((prev) => prev || response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading holiday groups:', error);
      toast.error('Failed to load holiday groups');
    }
  };

  const loadHolidays = async () => {
    if (!selectedHolidayGroup) return;
    setLoading(true);
    try {
      const response = await holidayGroupAPI.getHolidays(selectedHolidayGroup, selectedYear);
      const sorted = [...response.data].sort((a, b) => (a.date < b.date ? -1 : 1));
      setHolidays(sorted);
    } catch (error) {
      console.error('Error loading holidays:', error);
      toast.error('Failed to load holidays');
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = () => {
    setEditingHoliday(null);
    setFormData({ name: '', date: '', holiday_group_id: selectedHolidayGroup || '' });
    setShowModal(true);
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({ name: holiday.name, date: holiday.date, holiday_group_id: holiday.holiday_group_id });
    setShowModal(true);
  };

  const handleDeleteHoliday = async (holiday) => {
    if (!window.confirm(`Delete "${holiday.name}" (${holiday.date})?`)) return;
    try {
      await holidayAPI.delete(holiday.id);
      toast.success(`"${holiday.name}" deleted`);
      await loadHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      toast.error(parseApiError(error));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const yearFromDate = parseInt(formData.date.split('-')[0]);
      if (editingHoliday) {
        const updateData = {};
        if (formData.name !== editingHoliday.name) updateData.name = formData.name;
        if (formData.date !== editingHoliday.date) {
          updateData.date = formData.date;
          updateData.year = yearFromDate;
        }
        if (parseInt(formData.holiday_group_id) !== editingHoliday.holiday_group_id) {
          updateData.holiday_group_id = parseInt(formData.holiday_group_id);
        }
        await holidayAPI.update(editingHoliday.id, updateData);
        toast.success(`"${formData.name}" updated`);
      } else {
        await holidayAPI.create({
          name: formData.name,
          date: formData.date,
          holiday_group_id: parseInt(formData.holiday_group_id),
          year: yearFromDate,
        });
        toast.success(`"${formData.name}" added`);
      }
      setShowModal(false);
      await loadHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);
      toast.error(parseApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    setFormData({ name: '', date: '', holiday_group_id: '' });
  };

  const formatLong = (dateString) => {
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const todayStr = (() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
  })();

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => currentYear - 2 + i);
  };

  const selectedGroupName = holidayGroups.find((g) => g.id === selectedHolidayGroup)?.name || '';
  const upcoming = holidays.filter((h) => h.date >= todayStr).length;

  return (
    <Container>
      <HeaderCard>
        <div>
          <h1><FiGift size={24} /> Public Holidays</h1>
          <p>Holiday calendar per holiday group{canManage ? ' — add, edit, or remove holidays.' : ' (view only).'}</p>
        </div>
        {canManage && selectedHolidayGroup && (
          <ModernButton onClick={handleAddHoliday}><FiPlus size={16} /> Add holiday</ModernButton>
        )}
      </HeaderCard>

      <Card>
        <ControlsRow>
          <Field>
            <label>Holiday group</label>
            <Chips>
              {holidayGroups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className={selectedHolidayGroup === group.id ? 'active' : ''}
                  onClick={() => setSelectedHolidayGroup(group.id)}
                >
                  Group {group.name}
                </button>
              ))}
            </Chips>
          </Field>
          <Field>
            <label>Year</label>
            <ModernSelect value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} style={{ width: 'auto' }}>
              {generateYearOptions().map((year) => <option key={year} value={year}>{year}</option>)}
            </ModernSelect>
          </Field>
        </ControlsRow>

        {loading ? (
          <EmptyState><LoadingSpinner size="32px" /><div style={{ marginTop: 12 }}>Loading holidays…</div></EmptyState>
        ) : holidays.length === 0 ? (
          <EmptyState>
            {selectedHolidayGroup
              ? `No holidays for Group ${selectedGroupName} in ${selectedYear}.`
              : 'Select a holiday group to view holidays.'}
          </EmptyState>
        ) : (
          <>
            <CountNote>
              {holidays.length} holiday{holidays.length === 1 ? '' : 's'} in {selectedYear}
              {selectedYear === new Date().getFullYear() ? ` · ${upcoming} upcoming` : ''}
            </CountNote>
            <TableCard>
              <Table>
                <thead>
                  <tr>
                    <th>Holiday</th>
                    <th>Date</th>
                    <th>Status</th>
                    {canManage && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {holidays.map((holiday) => {
                    const isPast = holiday.date < todayStr;
                    const isToday = holiday.date === todayStr;
                    return (
                      <tr key={holiday.id} className={isPast ? 'past' : ''}>
                        <td><strong>{holiday.name}</strong></td>
                        <td>{formatLong(holiday.date)}</td>
                        <td>
                          {isToday ? <Badge variant="error">Today</Badge>
                            : isPast ? <Badge>Past</Badge>
                              : <Badge variant="success">Upcoming</Badge>}
                        </td>
                        {canManage && (
                          <td>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <ModernButton size="small" variant="outline" onClick={() => handleEditHoliday(holiday)}>Edit</ModernButton>
                              <ModernButton size="small" variant="secondary" onClick={() => handleDeleteHoliday(holiday)}>Delete</ModernButton>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableCard>
          </>
        )}
      </Card>

      {showModal && (
        <ModalOverlay onMouseDown={(e) => e.target === e.currentTarget && handleCloseModal()}>
          <ModalContent>
            <ModalTitleRow>
              <h3>{editingHoliday ? `Edit "${editingHoliday.name}"` : 'Add holiday'}</h3>
              <CloseButton type="button" onClick={handleCloseModal} aria-label="Close"><FiX size={16} /></CloseButton>
            </ModalTitleRow>
            <form onSubmit={handleSubmit}>
              <Field style={{ marginBottom: 16 }}>
                <label>Holiday name *</label>
                <ModernInput
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g. New Year's Day"
                />
              </Field>
              <Field style={{ marginBottom: 16 }}>
                <label>Date *</label>
                <ModernInput
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </Field>
              <Field style={{ marginBottom: 16 }}>
                <label>Holiday group *</label>
                <ModernSelect
                  value={formData.holiday_group_id}
                  onChange={(e) => setFormData({ ...formData, holiday_group_id: e.target.value })}
                  required
                >
                  <option value="">Select holiday group</option>
                  {holidayGroups.map((group) => (
                    <option key={group.id} value={group.id}>Group {group.name}</option>
                  ))}
                </ModernSelect>
              </Field>
              <ModalFooter>
                <ModernButton type="button" variant="secondary" onClick={handleCloseModal} disabled={saving}>Cancel</ModernButton>
                <ModernButton type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editingHoliday ? 'Update holiday' : 'Add holiday'}
                </ModernButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default PublicHolidaysPage;
